import * as React from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { X } from 'lucide-react';

import { cn } from '../lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipArrow = React.forwardRef<
    React.ComponentRef<typeof TooltipPrimitive.Arrow>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Arrow>
>(({ className, ...props }, ref) => (
    <TooltipPrimitive.Arrow
        ref={ref}
        className={cn('fill-tooltip -translate-y-px', className)}
        {...props}
    />
));
TooltipArrow.displayName = TooltipPrimitive.Arrow.displayName;

type TooltipContentProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    showClose?: boolean;
    onClose?: () => void;
    showArrow?: boolean;
};

const TooltipContent = React.forwardRef<
    React.ComponentRef<typeof TooltipPrimitive.Content>,
    TooltipContentProps
>(
    (
        { className, sideOffset = 4, showClose, onClose, children, showArrow = true, ...props },
        ref,
    ) => (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                ref={ref}
                sideOffset={sideOffset}
                className={cn(
                    'z-50 overflow-visible bg-tooltip text-tooltip-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-tooltip-content-transform-origin)',
                    showClose ? 'px-6 py-4 text-base rounded-lg' : 'px-3 py-1.5 text-xs rounded',
                    className,
                )}
                {...props}
            >
                {showArrow && <TooltipArrow />}
                {showClose && onClose && (
                    <button
                        onClick={onClose}
                        className="absolute right-1 top-1 p-1 opacity-50 hover:opacity-100 transition-opacity"
                        aria-label="Close"
                    >
                        <X className="size-4" />
                    </button>
                )}
                <div className={cn(showClose && 'pr-3')}>{children}</div>
            </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
    ),
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
